import React, { createContext, useContext, useState, useEffect } from 'react'
import { supabase } from '../utils/supabase'

const AdminAuthContext = createContext({})

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext)
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider')
  }
  return context
}

export const AdminAuthProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    checkAdminSession()
  }, [])

  const checkAdminSession = () => {
    try {
      const adminLoggedIn = localStorage.getItem('adminLoggedIn')
      const adminUserData = localStorage.getItem('adminUser')
      
      if (adminLoggedIn === 'true' && adminUserData) {
        const userData = JSON.parse(adminUserData)
        setAdminUser(userData)
      }
    } catch (error) {
      console.error('Admin session check error:', error)
      clearAdminSession()
    } finally {
      setLoading(false)
    }
  }

  const adminLogin = async (email, password) => {
    try {
      setLoading(true)
      
      // Supabase auth ile giriş yap
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      
      if (error) throw error
      
      if (!data.user) throw new Error('Kullanıcı bulunamadı')
      
      // Admin kontrolü yap
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('role, full_name')
        .eq('id', data.user.id)
        .single()
      
      if (profileError || !profileData) {
        throw new Error('Kullanıcı profili bulunamadı')
      }
      
      if (profileData.role !== 'admin') {
        // Admin değilse çıkış yap
        await supabase.auth.signOut()
        throw new Error('Bu hesap admin yetkisine sahip değil')
      }
      
      // Admin oturumu localStorage'a kaydet
      const adminUserData = {
        id: data.user.id,
        email: data.user.email,
        full_name: profileData.full_name,
        role: 'admin'
      }
      
      localStorage.setItem('adminLoggedIn', 'true')
      localStorage.setItem('adminUser', JSON.stringify(adminUserData))
      setAdminUser(adminUserData)
      
      // Supabase auth'dan çıkış yap (admin paneli localStorage kullanacak)
      await supabase.auth.signOut()
      
      return { success: true, error: null }
    } catch (error) {
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }

  const adminLogout = () => {
    clearAdminSession()
    setAdminUser(null)
  }

  const clearAdminSession = () => {
    localStorage.removeItem('adminLoggedIn')
    localStorage.removeItem('adminUser')
  }

  const isAdmin = () => {
    return adminUser && adminUser.role === 'admin'
  }

  const value = {
    adminUser,
    loading,
    adminLogin,
    adminLogout,
    isAdmin,
    clearAdminSession
  }

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  )
} 